import React from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import { FormData, FormErrors } from "./types";
import {
  ERROR_DOCUMENTO,
  validarDNI,
  validarEdad,
} from "../../features/shared/utils/validaciones";

interface TabHuespedesProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  showTitle?: boolean;
  guestInfoMissing?: boolean;
}

const TabHuespedes: React.FC<TabHuespedesProps> = ({
  formData,
  setFormData,
  setErrors,
  showTitle = true,
  guestInfoMissing = false,
}) => {
  const theme = useTheme();

  const handleAcompananteChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const newAcompanantes = [...formData.acompanantes];
    newAcompanantes[index] = {
      ...newAcompanantes[index],
      [field]: value,
    };

    // Validar campos
    const errors: Record<string, string> = {};
    if (field === "dni" && !validarDNI(value)) {
      errors[`acompanante_${index}_dni`] = ERROR_DOCUMENTO;
    }
    if (field === "edad" && !validarEdad(value)) {
      errors[`acompanante_${index}_edad`] =
        "La edad debe ser un número entre 0 y 120";
    }

    setErrors((prev) => ({ ...prev, ...errors }));
    setFormData((prev) => ({
      ...prev,
      acompanantes: newAcompanantes,
    }));
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Grid
      container
      spacing={{ xs: 1.15, sm: 1.5 }}
      sx={{
        alignContent: "flex-start",
        "& .MuiOutlinedInput-root": {
          minHeight: { xs: 38, sm: 36 },
        },
        "& .MuiInputBase-input": {
          py: { xs: 0.95, sm: 1 },
          fontSize: { xs: "0.84rem", sm: "0.875rem" },
        },
        "& .MuiInputLabel-root": {
          fontSize: { xs: "0.82rem", sm: "0.875rem" },
        },
      }}
    >
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{
            color: "primary.main",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            mb: { xs: 0.15, sm: 0.25 },
            fontSize: { xs: "0.82rem", sm: "0.9rem" },
          }}
        >
          Afiliado (huésped principal)
          {guestInfoMissing && (
            <Tooltip title="Falta huésped o afiliado" arrow>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "error.main",
                  display: "inline-block",
                }}
              />
            </Tooltip>
          )}
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 1 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Nombre"
              name="afiliado_nombre"
              value={formData.afiliado_nombre || ""}
              onChange={handleTextChange}
              inputProps={{ "aria-label": "Nombre del afiliado" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: { xs: 38, sm: 36 },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon
                      sx={{ color: "primary.main", fontSize: "1.1rem" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Apellido"
              name="afiliado_apellido"
              value={formData.afiliado_apellido || ""}
              onChange={handleTextChange}
              inputProps={{ "aria-label": "Apellido del afiliado" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: { xs: 38, sm: 36 },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon
                      sx={{ color: "primary.main", fontSize: "1.1rem" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="DNI"
              name="afiliado_dni"
              value={formData.afiliado_dni || ""}
              onChange={handleTextChange}
              inputProps={{ "aria-label": "DNI del afiliado" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: { xs: 38, sm: 36 },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon
                      sx={{ color: "primary.main", fontSize: "1.1rem" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      {showTitle && (
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "inline-block",
              mb: { xs: 0.25, sm: 0.5 },
              fontSize: { xs: "0.86rem", sm: "1rem" },
            }}
          >
            <PeopleIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Acompañantes
          </Typography>
        </Grid>
      )}

      {formData.acompanantes.map((acompanante, index) => (
        <Grid item xs={12} key={index}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.15, sm: 1.5 },
              position: "relative",
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.03)
                  : theme.palette.grey[50],
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: { xs: 0.5, sm: 1 },
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2" color="primary">
                Acompañante {index + 1}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  const newAcompanantes = [...formData.acompanantes];
                  newAcompanantes.splice(index, 1);
                  setFormData((prev) => ({
                    ...prev,
                    acompanantes: newAcompanantes,
                  }));
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nombre"
                  value={acompanante.nombre}
                  inputProps={{
                    "aria-label": `Nombre acompañante ${index + 1}`,
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleAcompananteChange(index, "nombre", e.target.value);
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Apellido"
                  value={acompanante.apellido}
                  inputProps={{
                    "aria-label": `Apellido acompañante ${index + 1}`,
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleAcompananteChange(index, "apellido", e.target.value);
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="DNI/CUIT"
                  value={acompanante.dni}
                  inputProps={{
                    "aria-label": `DNI o CUIT acompañante ${index + 1}`,
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleAcompananteChange(index, "dni", e.target.value);
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Edad"
                  type="number"
                  value={acompanante.edad}
                  inputProps={{
                    "aria-label": `Edad acompañante ${index + 1}`,
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleAcompananteChange(index, "edad", e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormData((prev) => ({
              ...prev,
              acompanantes: [
                ...prev.acompanantes,
                {
                  nombre: "",
                  apellido: "",
                  dni: "",
                  edad: "",
                },
              ],
            }));
          }}
          size="small"
          sx={{ mt: { xs: 0.5, sm: 1 }, py: { xs: 0.45, sm: 0.5 } }}
        >
          Agregar Acompañante
        </Button>
      </Grid>
    </Grid>
  );
};

export default TabHuespedes;
